import type {
    Application,
    PaymentItem,
    PaymentPlan,
    PaymentAttempt,
    AttemptRecord,
    PaymentRecord,
    Program,
    FeesStructure,
    FeesSchedule,
    Company,
    DocumentRequirement,
    StudentPersonalDetails,
    AssociationRequest,
    Conversation,
    User,
    Student,
    Agreement,
} from '@/api';
import type { Dayjs } from 'dayjs';
import { Decimal } from 'decimal.js';
import { pick, startCase } from 'lodash';
import { match } from 'ts-pattern';

export type TimestampsAll<T extends string> = Partial<
    Record<`${T}_before` | `${T}_before_e` | `${T}_after` | `${T}_after_e`, Date | Dayjs | string | undefined> &
        Record<`${T}_between`, [Date | Dayjs | string, Date | Dayjs | string]>
>;

const inOrder: Application['status'][] = [
    'draft',
    'submitted',
    'in_review',
    'offered',
    'accepted',
    'confirmed',
    'completed',
];

const now = () => timeTravel(new Date());
export const timeTravel = (date: Date) => /* new Date(date.getTime() + 1000 * 60 * 60 * 24 * 100) */ date;

export class StudentUtil {
    public static name(
        student: Student | undefined | null,
        opts: { ref?: boolean; dob?: boolean } = { ref: true, dob: false },
    ): string {
        if (student == null) return '';

        let str = '';
        if (opts.ref) {
            str += student.reference_id ?? `#${student.id}`;
        }
        if (student.full_name) {
            str += ` ${student.full_name}`;
        }
        if (opts.dob && student.personal_details?.date_of_birth) {
            str += ` - ${new Date(student.personal_details.date_of_birth).toDateString()}`;
        }
        return str;
    }
}

export class AgreementUtil {
    public static isSigned(agreement: Agreement) {
        return agreement.status === 'signed';
    }

    public static isSignedBy(agreement: Agreement, by: Company) {
        return (
            AgreementUtil.isSigned(agreement) ||
            agreement.signatures?.every(s => (s.company_id === by.id ? !!s.signed_at : true))
        );
    }
}

export class AssociationRequestUtil {
    public static otherCompanyId(request: AssociationRequest, viewer?: Company): number | undefined {
        if (request.sender_company_id === viewer?.id) {
            return request.receiver_company_id;
        }
        if (request.receiver_company_id === viewer?.id) {
            return request.sender_company_id;
        }
        return undefined;
    }

    public static otherCompany(request: AssociationRequest, viewer?: Company): Company | undefined {
        if (request.sender_company_id === viewer?.id) {
            return request.receiver_company;
        }
        if (request.receiver_company_id === viewer?.id) {
            return request.sender_company;
        }
        return undefined;
    }

    public static status(request: AssociationRequest, viewer: Company) {
        if (request.agreement_id == null && request.status === 'pending') {
            return 'Waiting for Agreement';
        }
        if (request.status === 'interested') {
            return 'Expression of Interest Submitted';
        }
        if (request.renewal_agreement_id != null) {
            return 'Being Renewed';
        }
        if (request.status === 'rejected') {
            return 'Rejected' + (request.initiator_company_id !== viewer.id ? ' (by you)' : '');
        }
        if (request.status === 'cancelled') {
            return 'Canceled' + (request.initiator_company_id === viewer.id ? ' (by you)' : '');
        }
        return startCase(request.status);
    }

    public static help(request: AssociationRequest, viewer: Company): string | undefined {
        if (request.status === 'pending') {
            return match([request.agreement_id == null, viewer.company_type])
                .with([true, 'school'], () => 'Send an agreement to the agency.')
                .with([true, 'agency'], () => 'Waiting for the school to send an agreement.')
                .with([false, 'school'], () => 'Waiting for the agency to sign the agreement.')
                .with([false, 'agency'], () => 'Sign the agreement to become partners.')
                .otherwise(() => undefined);
        }
        if (request.renewal_agreement_id != null) {
            return match(viewer.company_type)
                .with('agency', () => 'Sign the renewal agreement.')
                .with('school', () => 'Waiting for the agency to sign the renewal agreement.')
                .otherwise(() => undefined);
        }
        if (request.status === 'rejected') {
            return match(request.initiator_company_id)
                .with(viewer.id, () => 'This request has been rejected.')
                .otherwise(() => 'You rejected this request.');
        }
        if (request.status === 'cancelled') {
            return match(request.initiator_company_id)
                .with(viewer.id, () => 'You cancelled this request.')
                .otherwise(() => 'This request has been cancelled.');
        }
        if (request.status === 'interested') {
            return match(request.initiator_company_id)
                .with(viewer.id, () => 'Waiting for the agency to send an application.')
                .otherwise(() => 'This school has expressed interest in a partnership. Send an application.');
        }
        if (request.status === 'accepted') {
            return 'This request has been accepted.';
        }
        if (request.status === 'expired') {
            return 'This request has expired.';
        }
        return startCase(request.status);
    }
}

export class FeesUtil {
    public static feesAttributes(obj: FeesStructure & FeesSchedule) {
        return pick(obj, [
            'advance_fees',
            'tuition_fees',
            'material_fees',
            'commission_bonus',
            'advance_fees',
            'application_fees',
            'discount',
            'duration_weeks',
            'number_of_payments',
            'regular_interval_unit',
            'regular_interval_width',
            'fees_currency',
            'schedule_type',
        ] satisfies (keyof (FeesStructure & FeesSchedule))[]);
    }
}

export class ApplicationUtil {
    static get COLLECTION_OFFER_LETTER() {
        return 'offer_letter';
    }
    static get COLLECTION_CONFIRMATION_OF_ENROLMENT() {
        return 'confirmation_of_enrolment';
    }
    static get COLLECTION_ACCEPTANCE_LETTER() {
        return 'acceptance_letter';
    }

    constructor(public readonly application?: Application) {}

    public isAfterStatus(status: Application['status']) {
        return this.application && ApplicationUtil.isAfterStatus(this.application, status);
    }

    public isBeforeStatus(status: Application['status']) {
        return this.application && ApplicationUtil.isBeforeStatus(this.application, status);
    }

    public isOfferLetterSigned() {
        return ApplicationUtil.isOfferLetterSigned(this.application);
    }

    public static isAfterStatus(app: Application, status: Application['status']) {
        return inOrder.indexOf(app.status) > inOrder.indexOf(status);
    }

    public static isBeforeStatus(app: Application, status: Application['status']) {
        return inOrder.indexOf(app.status) < inOrder.indexOf(status);
    }

    public static isOfferLetterSigned(app?: Application) {
        return (
            app?.offer_letters != null &&
            app.offer_letters.some(o => o.status === 'signed' || o.signatures?.some(s => s.signed_at != null))
        );
    }

    public static requiredDocumentsSatisfied(app?: Application) {
        return app?.document_requirements?.every(req => !req.required || DocumentRequirementUtil.isSatisfied(req));
    }
}

export class PersonalDetailsUtil {
    public static isSectionComplete(
        pd: StudentPersonalDetails | undefined | null,
        fields: (keyof StudentPersonalDetails)[],
    ) {
        return fields.every(
            key => pd != null && (!pd.required.includes(key) || pd[key as keyof StudentPersonalDetails] != null),
        );
    }

    public static isComplete(pd: StudentPersonalDetails | undefined | null) {
        return pd?.required.every(key => pd[key as keyof StudentPersonalDetails] != null) ?? false;
    }
}

export class DocumentRequirementUtil {
    public static isSatisfied(req: DocumentRequirement) {
        return req.last_submitted_at != null && (req.denied_at == null || req.last_submitted_at > req.denied_at);
    }

    public static isDenied(req: DocumentRequirement) {
        return req.denied_at != null && (req.last_submitted_at == null || req.last_submitted_at < req.denied_at);
    }

    public static isApproved(req: DocumentRequirement) {
        return req.approved_at != null && (req.denied_at == null || req.approved_at > req.denied_at);
    }
}

export class ProgramUtil {
    constructor(public readonly program: Program) {}

    public static applicable(program: Program) {
        return program.partnered && program.enabled;
    }
}

export class AttemptRecordUtil {
    constructor(public readonly attempt: AttemptRecord) {}

    public isPaid() {
        return AttemptRecordUtil.isPaid(this.attempt.status);
    }

    public static isPaid(status: AttemptRecord['status']) {
        return (status !== 'pending' && status !== 'refunded' && status !== 'cancelled') ?? false;
    }
}

export class PaymentRecordUtil {
    constructor(public readonly record: PaymentRecord) {}

    public isFulfilled() {
        return PaymentRecordUtil.isFulfilled(this.record);
    }

    public isPartiallyFulfilled() {
        return PaymentRecordUtil.isPartiallyFulfilled(this.record);
    }

    public isPending() {
        return PaymentRecordUtil.isPending(this.record);
    }

    public static isFulfilled(record: PaymentRecord) {
        return record.attempt_records?.every(v => AttemptRecordUtil.isPaid(v.status));
    }

    public static isPartiallyFulfilled(record: PaymentRecord) {
        return record.attempt_records?.some(v => AttemptRecordUtil.isPaid(v.status));
    }

    public static isPending(record: PaymentRecord) {
        return !PaymentRecordUtil.isFulfilled(record);
    }
}

export class PaymentAttemptUtil {
    constructor(public readonly attempt: PaymentAttempt) {}

    /**
     * isFulfilled
     */
    public isPaid() {
        return PaymentAttemptUtil.isPaid(this.attempt);
    }

    public static isPaid(attempt: PaymentAttempt) {
        return attempt.status === 'fulfilled' || attempt.status === 'available' || attempt.status === 'distributed';
    }

    public static canBePaid(attempt: PaymentAttempt): attempt is Omit<PaymentAttempt, 'client_secret'> & {
        client_secret: string;
    } {
        return (attempt.can?.pay ?? true) && attempt.status === 'pending' && attempt.client_secret != null;
    }

    public static canBeCancelled(attempt: PaymentAttempt) {
        return attempt.status === 'pending' && attempt.can?.cancel;
    }
}

export class PaymentItemUtil {
    constructor(public readonly item: PaymentItem) {}

    public isPaid() {
        return PaymentItemUtil.isPaid(this.item);
    }

    public static canMakeAttempt(item: PaymentItem) {
        return item.can?.attempt && !PaymentItemUtil.processableAmount(item).eq(0);
    }

    public static paidAmount(item: PaymentItem) {
        if (item.payment_attempts == null) {
            return new Decimal(0);
        }
        return (
            item.payment_attempts
                ?.filter(attempt => PaymentAttemptUtil.isPaid(attempt))
                .reduce((carry, current) => carry.plus(current.linked_amount ?? '0'), new Decimal(0)) ?? new Decimal(0)
        );
    }

    public static unpaidAmount(item: PaymentItem) {
        if (item.payment_attempts == null) {
            return item.due_amount;
        }
        return PaymentItemUtil.paidAmount(item).negated().plus(item.due_amount);
    }

    public static processableAmount(item: PaymentItem) {
        if (item.payment_attempts == null) {
            return new Decimal(0);
        }
        return PaymentItemUtil.paidAmount(item)
            .plus(
                item.payment_attempts
                    .filter(attempt => attempt.status === 'pending')
                    .reduce((carry, current) => carry.plus(current.amount ?? '0'), new Decimal(0)) ?? new Decimal(0),
            )
            .negated()
            .plus(item.due_amount);
    }

    public static isPaid(item: PaymentItem) {
        if (item.payment_attempts == null) {
            return false;
        }
        return PaymentItemUtil.paidAmount(item).gte(item.due_amount);
    }

    public static isPartiallyPaid(item: PaymentItem) {
        if (item.payment_attempts == null) {
            return false;
        }
        return PaymentItemUtil.paidAmount(item).gt(0);
    }

    public static isUpcoming(item: PaymentItem) {
        return item.due_on > now();
    }

    public static isPast(item: PaymentItem) {
        return item.due_on < now();
    }

    public static isNextDue(item: PaymentItem, otherItems: PaymentItem[] | undefined) {
        return (
            otherItems?.every(
                other => other.index >= item.index || other.due_on < now() || PaymentItemUtil.isPaid(other),
            ) ?? false
        );
    }

    public static isNextUnpaid(item: PaymentItem, otherItems?: PaymentItem[]) {
        return (
            otherItems?.every(
                other => other.index >= item.index || (other.due_on < item.due_on && PaymentItemUtil.isPaid(other)),
            ) ?? false
        );
    }

    public static status(item: PaymentItem, allItems?: PaymentItem[]) {
        if (PaymentItemUtil.isPaid(item)) {
            return 'Paid';
        }
        const partially = PaymentItemUtil.isPartiallyPaid(item);
        if (item.due_on < now()) {
            return partially ? 'Overdue (Partially Paid)' : 'Overdue';
        }
        const dueNext = PaymentItemUtil.isNextDue(item, allItems)
            ? new Date(item.due_on).getTime() - now().getTime() > 24 * 60 * 60 * 1000 * 7
                ? ' (Due Soon)'
                : ' (Due Next)'
            : '';
        if (partially) {
            return dueNext ? 'Partially Paid (Due Soon)' : 'Partially Paid';
        }
        return dueNext ? 'Unpaid (Due Soon)' : 'Unpaid';
    }
}

export class PaymentPlanUtil {
    constructor(public readonly plan: PaymentPlan) {}

    public static isLocked(plan: PaymentPlan) {
        return plan.payment_items == null || plan.payment_items.length === 0;
    }

    public static totalPaid(plan: PaymentPlan) {
        return (
            plan.payment_attempts
                ?.filter(attempt => PaymentAttemptUtil.isPaid(attempt))
                .map(attempt => new Decimal(attempt.amount))
                .reduce((carry, current) => carry.plus(current), new Decimal(0)) ?? new Decimal(0)
        );
    }

    public static advanceFeesCleared(plan: PaymentPlan) {
        return new Decimal(plan.advance_fees).lte(PaymentPlanUtil.totalPaid(plan));
    }

    public static totalOwed(plan: PaymentPlan) {
        return (
            plan.payment_items?.reduce((carry, current) => carry.plus(current.due_amount), new Decimal(0)) ??
            new Decimal(0)
        );
    }

    public static totalOwedNow(plan: PaymentPlan) {
        return (
            plan.payment_items
                ?.filter(
                    item => !PaymentItemUtil.isUpcoming(item) || PaymentItemUtil.isNextDue(item, plan.payment_items),
                )
                .map(item => new Decimal(item.due_amount))
                .reduce((carry, current) => carry.plus(current), new Decimal(0)) ?? new Decimal(0)
        );
    }

    public static balance(plan: PaymentPlan) {
        if (plan.payment_items == null || plan.payment_attempts == null) {
            console.warn('PaymentPlanUtil.balance: no items or attempts');
            return new Decimal(0);
        }
        const owed = PaymentPlanUtil.totalOwedNow(plan);
        const paid = PaymentPlanUtil.totalPaid(plan);
        return paid.minus(owed);
    }

    public static balanceUpto(time: Date, plan: PaymentPlan) {
        if (plan.payment_items == null || plan.payment_attempts == null) {
            console.warn('PaymentPlanUtil.balanceUpto: no items or attempts');
            return new Decimal(0);
        }
        const owed = plan.payment_items
            .map(i => (i.due_on >= time ? new Decimal(0) : new Decimal(i.due_amount)))
            .reduce((carry, current) => carry.plus(current), new Decimal(0));
        const paid = plan.payment_attempts
            .map(attempt => {
                if (
                    PaymentAttemptUtil.isPaid(attempt) &&
                    timeTravel(attempt.fulfilled_at ?? attempt.created_at) < time
                ) {
                    return new Decimal(attempt.amount);
                }
                return new Decimal(0);
            })
            .reduce((carry, current) => carry.plus(current), new Decimal(0));
        return paid.minus(owed);
    }

    public static balanceAfter(time: Date | string, plan: PaymentPlan) {
        const newTime = new Date(new Date(time).getTime() + 1000);
        return PaymentPlanUtil.balanceUpto(newTime, plan);
    }

    public static nextDueItem(plan: PaymentPlan) {
        return plan.payment_items
            ?.filter(item => item.due_on > now())
            .sort((a, b) => new Date(a.due_on).getTime() - new Date(b.due_on).getTime())?.[0];
    }

    public static nextUnpaidItem(plan: PaymentPlan) {
        let paid = PaymentPlanUtil.totalPaid(plan);
        for (const item of plan.payment_items ?? []) {
            if (paid.gte(item.due_amount)) {
                paid = paid.minus(item.due_amount);
                continue;
            }
            return item;
        }
        return undefined;
    }

    public static firstUnpaidItem(plan: PaymentPlan) {
        return PaymentPlanUtil.nextUnpaidItem(plan);
    }

    public static upcomingItems(plan: PaymentPlan) {
        const nextDue = PaymentPlanUtil.nextDueItem(plan);
        if (nextDue == null) return [];
        return (
            plan.payment_items
                ?.filter(p => p.due_on > now() && p.id !== nextDue.id)
                ?.sort((a, b) => new Date(a.due_on).getTime() - new Date(b.due_on).getTime()) ?? []
        );
    }

    public static sensibleAmountsToPay(plan: PaymentPlan) {
        const remainingOwed = PaymentPlanUtil.totalOwed(plan).minus(PaymentPlanUtil.totalPaid(plan)).absoluteValue();
        const nextUnpaid = PaymentPlanUtil.nextUnpaidItem(plan);
        const balance = PaymentPlanUtil.balance(plan);

        return {
            'Total Due': remainingOwed,
            'Current Due': balance.lte(0) ? balance.negated() : 0,
            [`Next Due (${nextUnpaid?.due_on.toLocaleDateString()})`]: nextUnpaid?.due_amount ?? new Decimal(0),
            Balance: balance.gt(0) ? `+${balance}` : `${balance}`,
        };
    }
}
